/**
 * Agent management panel: agent list first, the guided Add agent flow, and detail editing.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Bot,
  Pencil,
  Plus,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import { useAgentVersion, useAgents, useDeleteAgent, useUpdateAgent } from '@/hooks/useAgents';
import AddAgentWizard from '@/components/agents/AddAgentWizard';
import AgentUpdatePanel from '@/components/agents/AgentUpdatePanel';
import { IMPORTANCE_BADGES, updateFor } from '@/components/agents/agentUpdates';
import { useMyUsage } from '@/hooks/useTiers';
import type { Agent } from '@/types/agent';
import { formatDate } from '@/utils/formatters';
import { coreApiMode } from '@/config/deployment';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CONNECTION_LABELS: Record<string, string> = {
  long_poll: 'Long Poll',
  websocket: 'WebSocket',
};

function StatusBadge({ status }: { status: string }) {
  const isOnline = status === 'online' || status === 'scanning';
  return (
    <span className="flex items-center gap-1.5">
      <span
        className={`inline-block h-2 w-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-gray-300'}`}
      />
      <span className={`text-xs font-medium ${isOnline ? 'text-green-700' : 'text-gray-500'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    </span>
  );
}







// ---------------------------------------------------------------------------
// Agent detail slide-over
// ---------------------------------------------------------------------------

function AgentDetailPanel({
  agent,
  allowedConnectionTypes,
  onClose,
  onSave,
  onDelete,
  isSaving,
  isDeleting,
}: {
  agent: Agent;
  allowedConnectionTypes: string[];
  onClose: () => void;
  onSave: (agentId: string, name: string, connectionType: string, setError: (e: string) => void) => void;
  onDelete: (agentId: string, name: string) => void;
  isSaving?: boolean;
  isDeleting?: boolean;
}) {
  const [name, setName] = useState(agent.name);
  const [connectionType, setConnectionType] = useState<string>(agent.connection_type);
  const [saveError, setSaveError] = useState('');
  const dirty = name !== agent.name || connectionType !== agent.connection_type;
  const connectionTypeChanged = connectionType !== agent.connection_type;

  // Reset if a different agent is selected
  useEffect(() => {
    setName(agent.name);
    setConnectionType(agent.connection_type);
    setSaveError('');
  }, [agent.id]);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/20"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-blue-600" />
            <h2 className="text-base font-semibold text-gray-900">Agent Details</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Status strip */}
          <div className="flex items-center justify-between rounded-lg bg-gray-50 border border-gray-200 px-4 py-3">
            <StatusBadge status={agent.status} />
            {agent.last_seen_at && (
              <span className="text-xs text-gray-400">
                Last seen {formatDate(agent.last_seen_at)}
              </span>
            )}
          </div>

          {/* Read-only metadata */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-0.5">Version</p>
              <p className="text-gray-800">{agent.version ?? '—'}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-0.5">Key Prefix</p>
              <p className="font-mono text-gray-800">{agent.api_key_prefix}…</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs font-medium text-gray-500 mb-0.5">Registered</p>
              <p className="text-gray-800">{formatDate(agent.created_at)}</p>
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Editable fields */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
              <Pencil className="h-3.5 w-3.5 text-gray-400" />
              Edit Agent
            </h3>

            {saveError && (
              <div className="rounded-md bg-red-50 px-4 py-2 text-sm text-red-700">{saveError}</div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
              <input
                className="input-field"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={255}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Connection Type
              </label>
              <select
                className="input-field"
                value={connectionType}
                onChange={(e) => setConnectionType(e.target.value)}
              >
                {allowedConnectionTypes.map((ct) => (
                  <option key={ct} value={ct}>
                    {CONNECTION_LABELS[ct] ?? ct}
                  </option>
                ))}
              </select>
              {connectionTypeChanged && (
                <p className="mt-1.5 text-xs text-amber-600">
                  Restart the agent container on your server for this change to take effect.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => onDelete(agent.id, agent.name)}
            disabled={isDeleting}
            className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-800 disabled:opacity-40"
          >
            <Trash2 className="h-4 w-4" />
            Remove agent
          </button>

          <div className="flex gap-3">
            <button onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={() => {
                setSaveError('');
                onSave(agent.id, name.trim(), connectionType, setSaveError);
              }}
              disabled={!dirty || isSaving || !name.trim()}
              className="btn-primary flex items-center gap-1.5"
            >
              <Save className="h-4 w-4" />
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AgentManager() {
  const { data: agents = [], isLoading } = useAgents();
  const { data: usage } = useMyUsage();
  const { data: versionData } = useAgentVersion();
  const updateMutation = useUpdateAgent();
  const deleteMutation = useDeleteAgent();

  const [adding, setAdding] = useState(false);
  const [updating, setUpdating] = useState<Agent | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);

  const allowedConnectionTypes = (usage?.tier_config?.allowed_connection_types ?? '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

  const canAdd = allowedConnectionTypes.length > 0;
  const platformUrl = window.location.origin;
  const agentVersion = versionData?.version ?? (coreApiMode?'1.4.0':'latest');
  const releases = versionData?.releases ?? [];
  const outdated = agents.filter((a) => updateFor(a.version, releases));

  function handleSave(
    agentId: string,
    name: string,
    connectionType: string,
    setError: (e: string) => void,
  ) {
    updateMutation.mutate(
      { agentId, payload: { name, connection_type: connectionType } },
      {
        onSuccess: (updated) => setSelectedAgent(updated),
        onError: (err: any) => {
          setError(err?.response?.data?.detail ?? 'Failed to save changes.');
        },
      },
    );
  }

  function handleDelete(agentId: string, name: string) {
    if (confirm(`Remove agent "${name}"? It will stop receiving scan jobs.`)) {
      deleteMutation.mutate(agentId, {
        onSuccess: () => setSelectedAgent(null),
      });
    }
  }

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading agents…</div>;
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Remote Agents</h2>
        {canAdd && (
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add agent
          </button>
        )}
      </div>

      {/* Plan gate */}
      {!canAdd && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
          Remote agents are not available on your current plan. Upgrade to Starter or higher to
          add on-premises scanning agents.
        </div>
      )}

      {adding && (
        <AddAgentWizard allowedConnectionTypes={allowedConnectionTypes} agentVersion={agentVersion} platformUrl={platformUrl} onClose={() => setAdding(false)} />
      )}

      {outdated.length > 0 && (
        <div role="status" className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {outdated.length === 1 ? '1 agent can' : `${outdated.length} agents can`} be updated to {agentVersion}. Select <strong>Update</strong> next to an agent for the steps.
        </div>
      )}

      {/* Agent table */}
      {agents.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-gray-200 p-8 text-center">
          <Bot className="h-8 w-8 text-gray-400" />
          <p className="text-sm font-medium text-gray-600">No agents registered</p>
          <p className="text-xs text-gray-400">
            Select Add agent to register one and get step-by-step setup instructions.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Connection</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Last Seen</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Key Prefix</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {agents.map((agent) => (
                <tr
                  key={agent.id}
                  onClick={() => setSelectedAgent(agent)}
                  className="hover:bg-blue-50 cursor-pointer transition-colors"
                >
                  <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                    {agent.name}
                    {agent.version && (
                      <span className="ml-2 text-xs text-gray-400">v{agent.version}</span>
                    )}
                    {(() => {
                      const update = updateFor(agent.version, releases);
                      if (!update) return null;
                      const tone = update.importance === 'security' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800';
                      return <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>{IMPORTANCE_BADGES[update.importance]}</span>;
                    })()}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <StatusBadge status={agent.status} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                    {CONNECTION_LABELS[agent.connection_type] ?? agent.connection_type}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                    {agent.last_seen_at ? formatDate(agent.last_seen_at) : '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-500">
                    {agent.api_key_prefix}…
                  </td>
                  <td
                    className="whitespace-nowrap px-4 py-3 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {updateFor(agent.version, releases) && (
                      <button onClick={() => setUpdating(agent)} className="mr-2 rounded px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50">Update</button>
                    )}
                    <button
                      onClick={() => handleDelete(agent.id, agent.name)}
                      disabled={deleteMutation.isPending}
                      className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                      title="Remove agent"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-500">
        Setting up an agent? <strong>Add agent</strong> walks you through it, or read the{' '}
        <Link className="text-indigo-600 underline" to="/docs#agents-setup">full setup guide</Link>.
      </p>

      {updating && (
        <AgentUpdatePanel agent={updating} releases={releases} platformUrl={platformUrl} onClose={() => setUpdating(null)} />
      )}

      {/* Agent detail slide-over */}
      {selectedAgent && (
        <AgentDetailPanel
          agent={selectedAgent}
          allowedConnectionTypes={
            allowedConnectionTypes.length > 0
              ? allowedConnectionTypes
              : [selectedAgent.connection_type]
          }
          onClose={() => setSelectedAgent(null)}
          onSave={handleSave}
          onDelete={handleDelete}
          isSaving={updateMutation.isPending}
          isDeleting={deleteMutation.isPending}
        />
      )}
    </div>
  );
}
